import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isCustomFile } from '@follow/cdk'
import { MedicalEventFiltersProps } from './MedicalEventFilters.model'

import { MedicalEventContentTypeIcon, MedicalEventDocumentType } from '../../../model/MedicalEvent'
import { isImageExtension, isPrintableFileExtension } from '../../../misc/files.utilities'
import { Heading, IconButton, IconChip, IconsType, MenuChip, ValidationModal } from '../../shared'

import { FiltersWrapper } from '../../shared/filters/FiltersWrapper'
import { ContentFilters } from './ContentFilters'
import { CustomFile, FileKind } from '../../../model/File'

import styles from './MedicalEventFilters.module.scss'
import { AddContentButton } from '../../shared/buttons/AddContentButton'
import { useActionDispatch, useCurrentPatient, useWidthObserver } from '../../../hooks/utils'
import { breakMedium } from '../../../misc/responsive'
import { formatPrescriptionPrice, isPrescription } from '../../../misc/documents.utilities'
import { DocumentInstanceListItem } from '../../../model/DocumentInstance'
import { FwDocumentType } from '../../../model/Document'
import { EfficienceDmpUploadModal } from '../..//dmp/EfficienceDmpUploadModal'
import { useLocation } from 'react-router'
import { useDownloadDocuments, usePrintDocuments } from '../../../hooks/queries/document'
import { isDefined } from '../../../misc/functions.utilities'
import { dmpDomainActions, DmpRequestTypes } from '../../../store/domain/dmp'
import { EfficienceUploadConfig } from '../../../model/Efficience'
import { DMP_SERVICES_ACCEPTED_EXTENSIONS, mapFileForDMP } from '../../../misc/dmp.utilities'
import { useGetFile } from '../../../hooks/queries/files'
import { ColorsV2 } from '../../../design-system/colors_v2'
import { getDefaultBgColor } from '../../../misc/colors.utilities'
import { usePrintingContext } from '../../../core/hooks/usePrintingContext'
import { useDownloadingContext } from '../../../core/hooks/useDownloadingContext'

export const MedicalEventFilters: FunctionComponent<MedicalEventFiltersProps> = ({
  initializeFromUrl,
  lockDocument,
  medicalEvent,
  medicalEventDeleteDocument,
  medicalEventDocumentFilter,
  medicalEventSelectedDocument,
  selectMedicalEventDocument,
  updateDocument,
  renewPrescription,
  files,
  addFilesToMedicalEvent,
  deleteFromDMP,
  userEnabledFeatures,
  uploadToDMP,
}) => {
  const { currentPatient: patient } = useCurrentPatient()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isLowerThanBreakpoint = useWidthObserver(breakMedium)
  const [currentLocation] = useState(location)
  const [currentMedicalEvent] = useState(medicalEvent)
  const [documentToLock, setDocumentToLock] = useState<undefined | DocumentInstanceListItem>()
  const [displayRenewModal, setDisplayRenewModal] = useState<boolean>(false)
  const [pendingDmpUpload, setPendingDmpUpload] = useState<DocumentInstanceListItem | CustomFile>()
  const [pendingDmpDelete, setPendingDmpDelete] = useState<{
    id: number
    dmpDocumentId: string
    requestType: DmpRequestTypes
  }>()
  const { print } = usePrintDocuments()
  const { isLoading: isPrinting, currentItems } = usePrintingContext()
  const { download } = useDownloadDocuments()
  const { isLoading: isDownloading, currentItems: currentItemsDownloading } =
    useDownloadingContext()

  const {
    query: { data: file },
  } = useGetFile({
    fileId: pendingDmpUpload?.id,
    enabled: !!pendingDmpUpload && isCustomFile(pendingDmpUpload),
  })

  const sendFilesToDMP = useActionDispatch(dmpDomainActions.sendDocumentsToDMP)

  const selectedDocId =
    medicalEventDocumentFilter &&
    medicalEventDocumentFilter.medicalEventDocumentType === MedicalEventDocumentType.FW_DOCUMENT
      ? medicalEventDocumentFilter.id
      : null

  const selectedFileId =
    medicalEventDocumentFilter &&
    medicalEventDocumentFilter.medicalEventDocumentType === MedicalEventDocumentType.FILE
      ? medicalEventDocumentFilter.id
      : null

  const selectObservations =
    !!medicalEventDocumentFilter &&
    medicalEventDocumentFilter.medicalEventDocumentType === MedicalEventDocumentType.OBSERVATIONS

  const docsLength = medicalEvent.documents.length + medicalEvent.files.length

  const displayAddDocumentsButton =
    medicalEvent.isEditable &&
    isDefined(medicalEventDocumentFilter?.medicalEventDocumentType) &&
    medicalEventDocumentFilter?.medicalEventDocumentType !== MedicalEventDocumentType.ADDING

  // Query params can be ?docId={docId}&fileId={fileId}
  useEffect(() => {
    initializeFromUrl(currentLocation, currentMedicalEvent)
  }, [initializeFromUrl, currentLocation, currentMedicalEvent])

  const handleSelectMedicalEventDocument =
    (medicalEventDocumentType: MedicalEventDocumentType, id: number) => () => {
      if (id === selectedDocId) {
        return
      }
      selectMedicalEventDocument({
        medicalEventDocumentType,
        id,
        resetSelectedQuestionnaire: true,
      })
    }

  const handleEditDocumentTitle = (documentId: number, title: string, type: FwDocumentType) => {
    updateDocument(documentId, title, MedicalEventDocumentType.FW_DOCUMENT, type)
  }

  const handleEditFileTitle = (fileId: number, visibleName: string) => {
    updateDocument(fileId, visibleName, MedicalEventDocumentType.FILE)
  }

  const handleDeleteDocument = (id: number) => {
    if (window.confirm('Êtes vous sûr(e) de vouloir supprimer le document ?')) {
      medicalEventDeleteDocument(id, MedicalEventDocumentType.FW_DOCUMENT)
    }
  }

  const handleDeleteFile = (id: number) => {
    if (window.confirm('Êtes vous sûr(e) de vouloir supprimer le fichier ?')) {
      medicalEventDeleteDocument(id, MedicalEventDocumentType.FILE)
    }
  }

  const handleDownloadFile = useCallback(
    (file: CustomFile) => download({ files: [file] }),
    [download],
  )

  const handlePrintFile = useCallback((file: CustomFile) => print({ files: [file] }), [print])

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target && event.target.files) {
      const files = Array.from(event.target.files) as File[]
      addFilesToMedicalEvent(files)
    }
  }

  const handleAddDocument = useCallback(() => {
    selectMedicalEventDocument({ medicalEventDocumentType: MedicalEventDocumentType.ADDING })
  }, [selectMedicalEventDocument])

  const handleLockDocument = () => {
    if (documentToLock) {
      lockDocument(documentToLock?.id, !documentToLock?.locked)
    }
    setDocumentToLock(undefined)
  }

  const handleSendDocumentsToDMP = useCallback(
    async (config: EfficienceUploadConfig) => {
      if (pendingDmpUpload) {
        if (isCustomFile(pendingDmpUpload)) {
          const fileForDmp = file && mapFileForDMP(file)

          patient &&
            fileForDmp &&
            sendFilesToDMP(patient, medicalEvent.owner, config, DmpRequestTypes.SEND_FILES, [
              fileForDmp,
            ])
        } else {
          uploadToDMP(
            pendingDmpUpload.id,
            pendingDmpUpload.type,
            config,
            pendingDmpUpload.type === 'pdf'
              ? DmpRequestTypes.SEND_DOCUMENT_PDF_FORM
              : DmpRequestTypes.SEND_DOCUMENT,
          )
        }

        setPendingDmpUpload(undefined)
      }
    },
    [file, medicalEvent.owner, patient, pendingDmpUpload, sendFilesToDMP, uploadToDMP],
  )

  const handleDeleteDocumentFromDMP = useCallback(
    ({ healthcareSettingsId }: EfficienceUploadConfig) => {
      if (pendingDmpDelete && patient?.inseeNumber) {
        deleteFromDMP(
          pendingDmpDelete.id,
          pendingDmpDelete.dmpDocumentId,
          patient.inseeNumber,
          pendingDmpDelete.requestType,
          healthcareSettingsId,
        )
      }
      setPendingDmpDelete(undefined)
    },
    [deleteFromDMP, patient?.inseeNumber, pendingDmpDelete],
  )

  const displayQuestionnaireFilter =
    medicalEventSelectedDocument?.type === MedicalEventDocumentType.FW_DOCUMENT &&
    medicalEventSelectedDocument.item.type === 'farte'

  const testId = useMemo(
    () =>
      documentToLock?.locked
        ? 'modal-medical_event_filters-document_instance_unlock'
        : 'modal-medical_event_filters-document_instance_lock',
    [documentToLock?.locked],
  )

  const defaultBorderColor = getDefaultBgColor()
  return (
    <>
      <div className={styles.container}>
        <Heading size={5} theme="dark" weight="regular">
          <span className={styles.label}>Documents</span>
          <span className={styles.counter}>{docsLength}</span>
        </Heading>
        <div className={styles.content}>
          <FiltersWrapper variance="variant1">
            <IconChip
              onClick={handleSelectMedicalEventDocument(
                MedicalEventDocumentType.OBSERVATIONS,
                medicalEvent.id,
              )}
              selected={selectObservations}
              icon="editAlt"
              label="Observations"
              testId="icon_chip-observation-show"
              borderColor={defaultBorderColor}
            />
            <div className={styles.separator} />
            {medicalEvent.documents.map((documentInstance, index) => (
              <MenuChip
                key={index}
                onClick={handleSelectMedicalEventDocument(
                  MedicalEventDocumentType.FW_DOCUMENT,
                  documentInstance.id,
                )}
                isPrinting={isPrinting}
                currentPrintingItems={currentItems}
                isDownloading={isDownloading}
                currentDownloadingItems={currentItemsDownloading}
                testId={`button-chip-document-${documentInstance.id}`}
                id={documentInstance.id}
                collapsed={documentInstance.id !== selectedDocId}
                selected={documentInstance.id === selectedDocId}
                downloadConfig={
                  userEnabledFeatures?.cda
                    ? [
                        {
                          label: 'PDF',
                          onClick: () =>
                            download({
                              documents: [documentInstance],
                            }),
                        },
                        {
                          label: 'CDA',
                          onClick: () =>
                            download({
                              documents: [documentInstance],
                              format: 'cda',
                            }),
                        },
                      ]
                    : () =>
                        download({
                          documents: [documentInstance],
                        })
                }
                onPrint={() =>
                  print({
                    documents: [documentInstance],
                  })
                }
                onSendToDMP={
                  userEnabledFeatures?.dmpInApp
                    ? () => setPendingDmpUpload(documentInstance)
                    : undefined
                }
                onDeleteFromDMP={
                  userEnabledFeatures?.dmpInApp && documentInstance.dmpDocumentId
                    ? () => {
                        if (documentInstance.dmpDocumentId)
                          setPendingDmpDelete({
                            id: documentInstance.id,
                            dmpDocumentId: documentInstance.dmpDocumentId,
                            requestType: DmpRequestTypes.DELETE_DOCUMENT,
                          })
                      }
                    : undefined
                }
                onDelete={
                  medicalEvent.isDeletable
                    ? () => handleDeleteDocument(documentInstance.id)
                    : undefined
                }
                onLock={() => setDocumentToLock(documentInstance)}
                onTitleSave={
                  !documentInstance.locked && medicalEvent.isEditable
                    ? (title) =>
                        handleEditDocumentTitle(documentInstance.id, title, documentInstance.type)
                    : undefined
                }
                icon={(documentInstance?.documentCategory?.icon || '') as IconsType}
                label={documentInstance.title}
                onRenewal={
                  documentInstance.type === 'farte' && isPrescription(documentInstance.category)
                    ? () => setDisplayRenewModal(true)
                    : undefined
                }
                sublabel={
                  documentInstance.price
                    ? formatPrescriptionPrice(documentInstance.price)
                    : undefined
                }
                isLock={documentInstance.locked}
                disableLocker={
                  !medicalEvent.isEditable ||
                  (documentInstance.type === 'farte' &&
                    (!!documentInstance.renewedAt || !!documentInstance.printedAt) &&
                    isPrescription(documentInstance.category))
                }
                colorPreset="dark"
                borderColor={
                  (documentInstance?.documentCategory?.color as ColorsV2) || defaultBorderColor
                }
              />
            ))}
            <ValidationModal
              display={!!documentToLock}
              icon="lock"
              confirmLabel="Valider"
              title={
                documentToLock?.locked
                  ? 'Voulez vous vraiment déverrouiller ce document ? Son contenu ainsi que les valeurs de ses variables seront éditables et mises à jour avec les nouvelles données du patient'
                  : 'Voulez vous vraiment verrouiller ce document ? Son contenu ainsi que les valeurs de ses variables seront figées'
              }
              onSubmit={() => handleLockDocument()}
              onClose={() => setDocumentToLock(undefined)}
              testId={testId}
            />
            {medicalEvent.files.map((file) => {
              const type = isImageExtension(file.extension) ? FileKind.IMAGE : FileKind.FILE

              return (
                <MenuChip
                  key={file.id}
                  testId={`button-chip-file-${file.id}`}
                  onClick={handleSelectMedicalEventDocument(MedicalEventDocumentType.FILE, file.id)}
                  collapsed={file.id !== selectedFileId}
                  selected={file.id === selectedFileId}
                  disabled={!file.isOpenable}
                  isPrinting={isPrinting}
                  currentPrintingItems={currentItems}
                  isDownloading={isDownloading}
                  currentDownloadingItems={currentItemsDownloading}
                  onPrint={
                    isPrintableFileExtension(file.extension)
                      ? () => handlePrintFile(file)
                      : undefined
                  }
                  downloadConfig={() => handleDownloadFile(file)}
                  onDelete={medicalEvent.isDeletable ? () => handleDeleteFile(file.id) : undefined}
                  onTitleSave={
                    medicalEvent.isEditable
                      ? (title) => handleEditFileTitle(file.id, title)
                      : undefined
                  }
                  icon={
                    (file?.documentCategory?.icon || MedicalEventContentTypeIcon[type]) as IconsType
                  }
                  id={file.id}
                  borderColor={(file?.documentCategory?.color as ColorsV2) || defaultBorderColor}
                  label={file.visibleName}
                  colorPreset={file.critical ? 'danger' : 'dark'}
                  disablePrintDownload={!files[file.id]}
                  onSendToDMP={
                    userEnabledFeatures?.dmpInApp &&
                    DMP_SERVICES_ACCEPTED_EXTENSIONS.includes(file.extension)
                      ? () => setPendingDmpUpload(file)
                      : undefined
                  }
                  onDeleteFromDMP={
                    userEnabledFeatures?.dmpInApp && file.dmpId
                      ? () => {
                          if (file.dmpId)
                            setPendingDmpDelete({
                              id: file.id,
                              dmpDocumentId: file.dmpId,
                              requestType: DmpRequestTypes.DELETE_FILE,
                            })
                        }
                      : undefined
                  }
                />
              )
            })}
          </FiltersWrapper>
          <div className={styles.addDocument}>
            {displayAddDocumentsButton && (
              <AddContentButton
                testId="button-document-add"
                label="Document"
                isSmallScreen={isLowerThanBreakpoint}
                onClick={handleAddDocument}
              />
            )}
            <div className={styles.fileLoad}>
              <IconButton
                icon="paperClip"
                theme="primary"
                onClick={() => {
                  fileInputRef.current && fileInputRef.current.click()
                }}
                testId="button-medical_event-file-add"
              />
              <input
                ref={fileInputRef}
                autoComplete="off"
                className={styles.browse}
                onChange={handleFileUpload}
                multiple={true}
                tabIndex={-1}
                type="file"
              />
            </div>
          </div>
        </div>
      </div>
      {displayQuestionnaireFilter && <ContentFilters collapsed={!selectedDocId} />}
      <ValidationModal
        display={displayRenewModal}
        title="L'ordonnance d'origine sera verrouillée définitivement, et l'ordonnance renouvelée sera ajoutée à un nouvel évènement médical en date du jour."
        onSubmit={() => {
          selectedDocId && renewPrescription(selectedDocId)
          setDisplayRenewModal(false)
        }}
        onClose={() => {
          setDisplayRenewModal(false)
        }}
        testId="modal-prescription-renew"
      />
      <EfficienceDmpUploadModal
        display={!!pendingDmpUpload}
        onClose={() => setPendingDmpUpload(undefined)}
        onValid={handleSendDocumentsToDMP}
      />
      <EfficienceDmpUploadModal
        isDeletion
        display={!!pendingDmpDelete}
        onClose={() => setPendingDmpDelete(undefined)}
        onValid={handleDeleteDocumentFromDMP}
      />
    </>
  )
}
